import React, { useContext, useState, useMemo } from "react";
import { FaExpand } from "react-icons/fa";
import { StockTableData } from "src/helpers";
import { Firm, FirmDetails } from "pages/analyze-stock";
import AppContext from "src/components/AppContext";
import { getSortIcon, sortArray, SortConfig } from "src/helpers/api/sortconfig";
import {
  formatCurrencyWithLocalString,
  formatPercentageWithLocalString,
} from "src/helpers/numberFormatter";
import { getFormattedDate } from "src/helpers/dateFormatter";
import GaugeChart from "src/common/charts/gaugeChart";
import ChartModal from "src/components/patternInsight/chartModal";
import { DataPoint, fetchWrapper } from "src/helpers";
import { FlashingValue } from "src/common/FlashingValue";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";
import moment from "moment-timezone";
import { alertService } from "src/services";
interface PerformanceTableProps {
  records?: StockTableData[];
  firms?: Firm[];
  score: number;
  isResearchFirm?: boolean;
  withFundamentals?: boolean;
  onExpand?: () => void;
  onExpandHistorical?: () => void;
  hideResearchFirms?: boolean;
  hideGauge?: boolean;
  fullSize?: boolean;
  isDashboardChart?: boolean;
  tableHeightClass?: string;
}

const PerformanceTable = (props: PerformanceTableProps) => {
  const {
    records,
    firms,
    score,
    isResearchFirm,
    withFundamentals,
    hideResearchFirms,
    hideGauge,
    onExpand,
    onExpandHistorical,
    fullSize,
    isDashboardChart,
    tableHeightClass,
  } = props;

  const isCompactResearch = Boolean(
    isResearchFirm && isDashboardChart && !fullSize,
  );
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const context = useContext(AppContext);
  const { darkMode } = context.state;

  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [chartStock, setChartStock] = useState<string | null>(null);
  const [lineChartData, setLineChartData] = useState<DataPoint[]>([]);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [selectedRecordDate, setSelectedRecordDate] = useState<Date | null>(
    null,
  );

  const handleChartClick = async (symbol: string, date: Date | string) => {
    setChartStock(symbol);
    const selectedDate = new Date(date);
    const formattedDate = moment(selectedDate)
      .utcOffset("+05:30")
      .format("YYYY-MM-DD");
    setSelectedRecordDate(selectedDate);
    setIsChartModalOpen(true);
    setIsLoadingChart(true);
    try {
      const user = getLocalStorageUser();
      const response: { data: DataPoint[] } = await fetchWrapper.get(
        `/api/dashboard/ohlcdata?name=${encodeURIComponent(symbol)}&forPeakReturn=true&date=${formattedDate}`,
        undefined,
        {
          Authorization: `Bearer ${user.token}`,
        },
      );
      setLineChartData(response.data || []);
    } catch (error) {
      console.error("Error fetching chart data:", error);
      alertService.error("Failed to fetch chart data");
      setLineChartData([]);
    } finally {
      setIsLoadingChart(false);
    }
  };

  const closeModal = () => {
    setIsChartModalOpen(false);
    setChartStock(null);
    setLineChartData([]);
    setSelectedRecordDate(null);
  };

  const toggleRow = (symbol: string) => {
    setExpandedRows((prevState) => {
      // If the clicked row is already expanded, collapse it
      if (prevState[symbol]) {
        return {};
      }
      // Otherwise, expand the clicked row and collapse all others
      return { [symbol]: true };
    });
  };

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "",
    direction: null,
  });

  const sortedFirms = useMemo(
    () => sortArray((firms || []) as Firm[], sortConfig),
    [firms, sortConfig],
  );
  const sortedRecords = useMemo(
    () => sortArray(records || [], sortConfig),
    [records, sortConfig],
  );

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        let newDirection: SortConfig["direction"] = null;
        if (prev.direction === null) newDirection = "desc";
        else if (prev.direction === "desc") newDirection = "asc";
        else if (prev.direction === "asc") newDirection = null;

        return { key, direction: newDirection };
      }

      return { key, direction: "desc" };
    });
  };

  const getAvgTragetPercent = (details: FirmDetails[]) => {
    // Filter out firms that have any of these flags set to true
    const filtered = details.filter(
      (detail) =>
        !detail.target_achieved &&
        !detail.hold &&
        !detail.neutral &&
        !detail.accumulate,
    );

    if (filtered.length === 0) return "-";

    const total = filtered.reduce(
      (acc, detail) => acc + detail.percent_achieved,
      0,
    );
    const avg = total / filtered.length;
    return `${avg.toFixed(2)}%`; //avg.toFixed(2);
  };

  const getUpsideRemaining = (detail: FirmDetails) => {
    if (detail.target_achieved) return "Achieved";
    if (detail.hold) return "Hold";
    if (detail.neutral) return "Neutral";
    if (detail.accumulate) return "Accumulate";

    const percent_achieved = detail.percent_achieved;
    const upside_remaining = percent_achieved;
    if (upside_remaining) {
      return `${upside_remaining.toFixed(2)}%`;
    }
    return `-`;
  };

  return (
    <div
      className={
        isCompactResearch
          ? "flex h-full min-h-0 w-full min-w-0 flex-col"
          : `grid grid-cols-1 ${
              (isResearchFirm && hideGauge) ||
              fullSize ||
              (hideResearchFirms && hideGauge)
                ? "lg:grid-cols-1"
                : !hideGauge && !hideResearchFirms
                  ? "xl:grid-cols-2 2xl:grid-cols-3"
                  : "xl:grid-cols-2 2xl:grid-cols-2"
            } gap-1`
      }
    >
      {/* Top Performing Stocks Table */}
      {!isResearchFirm && (
        <div className="bg-white dark:bg-transparent rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="px-2 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h2 className="text-sm font-semibold">
              Historical Performance - as picked by ABillion in past
            </h2>
            {onExpandHistorical && (
              <button
                onClick={onExpandHistorical}
                className="text-gray-500 hover:text-gray-800 dark:hover:text-white transition-colors cursor-pointer"
                title="View full size"
              >
                <FaExpand size={18} />
              </button>
            )}
          </div>
          <div
            className={`overflow-auto relative ${
              tableHeightClass
                ? tableHeightClass
                : fullSize
                  ? "h-[350px]"
                  : `h-[250px] ${withFundamentals ? "lg:h-[300px]" : ""}`
            }`}
          >
            <table className="w-full text-sm">
              <thead className="bg-[#f4f5f8] dark:bg-[#30324e] sticky top-0 z-20">
                <tr>
                  <th
                    className="px-2 py-2 text-left text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree group relative cursor-pointer"
                    onClick={() => handleSort("date")}
                  >
                    Date {getSortIcon(sortConfig, "date")}
                  </th>
                  <th className="px-2 py-2 text-left text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree">
                    Symbol
                  </th>
                  <th className="px-2 py-2 text-left text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree">
                    Close
                  </th>
                  <th className="px-2 py-2 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree">
                    Peak Return
                  </th>
                  <th className="px-2 py-2 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree">
                    Current Return
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRecords?.map((record, index) => (
                  <tr
                    key={index}
                    className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent cursor-pointer transition-colors duration-200 hover:bg-gray-100/60 dark:hover:bg-white/4"
                    onClick={() =>
                      handleChartClick(
                        record.symbol,
                        record.record_date || record.date,
                      )
                    }
                  >
                    <td className="px-2 py-2">
                      {getFormattedDate(record.date)}
                    </td>
                    <td className="px-2 py-2 font-normal">{record.symbol}</td>
                    <td className="px-2 py-2">
                      {formatCurrencyWithLocalString(record.close)}
                    </td>
                    <td className="py-2 px-2 text-center font-figtree">
                      <div className="flex flex-col items-center justify-center gap-0.5 group">
                        <span
                          className={`text-[13px] font-medium ${record.high_return_perc === "0.00" ? "text-gray-900 dark:text-white" : Number(record.high_return_perc) > 0 ? "text-[#00A85F] dark:text-green-400" : "text-red-500"}`}
                        >
                          {Number(record.high_return_perc) !== 0.0
                            ? `${Number(record.high_return_perc) > 0 ? "+" : ""}${formatPercentageWithLocalString(record.high_return_perc)}`
                            : "0.00%"}
                        </span>
                        <div className="flex items-center gap-1">
                          <FlashingValue
                            value={
                              record.highest_price_afterdate > 0
                                ? formatCurrencyWithLocalString(
                                    record.highest_price_afterdate,
                                  )
                                : Number(record.high_return_perc) > 0
                                  ? formatCurrencyWithLocalString(
                                      Number(record.high_return_perc),
                                    )
                                  : "-"
                            }
                            isPositive={
                              Number(record.high_return_perc) === 0
                                ? null
                                : Number(record.high_return_perc) > 0
                            }
                            className="text-[12px]"
                          />
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-2 text-center font-figtree">
                      <div className="flex flex-col items-center justify-center gap-0.5">
                        <span
                          className={`text-[13px] font-medium ${record.current_return_perc === 0 ? "text-gray-900 dark:text-white" : Number(record.current_return_perc) > 0 ? "text-[#00A85F] dark:text-green-400" : "text-red-500"}`}
                        >
                          {Number(record.current_return_perc) > 0 && "+"}
                          {formatPercentageWithLocalString(
                            record.current_return_perc,
                          )}
                        </span>
                        <FlashingValue
                          value={formatCurrencyWithLocalString(record.ltp)}
                          isPositive={
                            record.ltp &&
                            record.close &&
                            record.ltp - record.close === 0
                              ? null
                              : !(
                                  record.ltp &&
                                  record.close &&
                                  record.ltp - record.close < 0
                                )
                          }
                          className="text-[12px] font-medium"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Research Firm Insights Table */}
      {!hideResearchFirms && (
        <div
          className={
            isCompactResearch
              ? "flex min-h-0 flex-1 flex-col"
              : "rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-transparent"
          }
        >
          {!isCompactResearch && (
            <div className="flex items-center justify-between border-b border-gray-200 px-2 py-4 dark:border-gray-700">
              <h2 className="text-sm font-semibold">Research Firm Insights</h2>
              {onExpand && (
                <button
                  onClick={onExpand}
                  className="cursor-pointer text-gray-500 transition-colors hover:text-gray-800 dark:hover:text-white"
                  title="View full size"
                >
                  <FaExpand size={18} />
                </button>
              )}
            </div>
          )}
          <div
            className={
              isCompactResearch
                ? "min-h-0 flex-1 overflow-auto"
                : `relative overflow-auto ${
                    tableHeightClass
                      ? tableHeightClass
                      : fullSize
                        ? "h-[350px]"
                        : `${isResearchFirm ? "lg:h-[300px]" : ""} ${withFundamentals ? "lg:h-[300px]" : ""}`
                  }`
            }
          >
            <table
              className={`w-full ${isCompactResearch ? "table-fixed text-[11px] leading-tight" : "text-sm"}`}
            >
              <thead className="sticky top-0 z-20 bg-[#f4f5f8] dark:bg-[#30324e]">
                <tr>
                  <th
                    className={`text-left font-medium font-figtree normal-case tracking-wider text-[#3D3D3D] dark:text-white ${isCompactResearch ? "w-[34%] px-1.5 py-1.5 text-[10px]" : "px-2 py-2 text-sm"}`}
                  >
                    Symbol
                  </th>
                  <th
                    className={`text-center font-medium font-figtree normal-case tracking-wider text-[#3D3D3D] dark:text-white ${isCompactResearch ? "w-[22%] px-1 py-1.5 text-[10px]" : "px-2 py-2 text-sm"}`}
                  >
                    {isCompactResearch ? "Avg" : "Avg. Target"}
                  </th>
                  <th
                    className={`text-center font-medium font-figtree normal-case tracking-wider text-[#3D3D3D] dark:text-white ${isCompactResearch ? "w-[18%] px-1 py-1.5 text-[10px]" : `px-2 py-2 text-sm ${isResearchFirm ? "w-auto" : "w-[5%]"}`}`}
                  >
                    Recent
                  </th>
                  <th
                    className={`group relative cursor-pointer text-center font-medium font-figtree normal-case tracking-wider text-[#3D3D3D] dark:text-white ${isCompactResearch ? "w-[26%] px-1 py-1.5 text-[10px]" : "w-[150px] px-2 py-2 text-sm"}`}
                    onClick={() => handleSort("latest_view")}
                  >
                    <span className="inline-flex items-center justify-center gap-0.5">
                      <span className={isCompactResearch ? "truncate" : ""}>
                        {isCompactResearch ? "Latest" : "Latest View Date"}
                      </span>
                      <span className="shrink-0">
                        {getSortIcon(sortConfig, "latest_view")}
                      </span>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedFirms.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-2 py-6 text-center text-xs text-gray-500"
                    >
                      No research data found
                    </td>
                  </tr>
                ) : (
                  sortedFirms.map((firm, index) => (
                    <React.Fragment key={index}>
                      <tr
                        className="cursor-pointer border-t border-gray-200 bg-white transition-colors duration-200 hover:bg-gray-100/60 dark:border-gray-700 dark:bg-transparent dark:hover:bg-white/4"
                        onClick={() => toggleRow(firm.stocksymbol)}
                      >
                        <td
                          className={`truncate font-normal ${isCompactResearch ? "px-1.5 py-1.5" : "px-2 py-2"}`}
                          title={firm.stocksymbol}
                        >
                          <span className="mr-1 inline-block w-3 shrink-0 text-center">
                            {expandedRows[firm.stocksymbol] ? "−" : "+"}
                          </span>
                          {firm.stocksymbol}
                        </td>
                        <td
                          className={`truncate text-center ${isCompactResearch ? "px-1 py-1.5" : "px-2 py-2"}`}
                        >
                          {getAvgTragetPercent(firm.details)}
                        </td>
                        <td
                          className={`truncate text-center ${isCompactResearch ? "px-1 py-1.5" : "px-2 py-2"} ${firm.dominant_sentiment == "Buy" ? "text-[#00A85F] dark:text-green-500" : "text-yellow-500"}`}
                        >
                          {firm.dominant_sentiment}
                        </td>
                        <td
                          className={`truncate text-center ${isCompactResearch ? "px-1 py-1.5" : "px-2 py-2"}`}
                        >
                          {getFormattedDate(firm.latest_view)}
                        </td>
                      </tr>
                      {expandedRows[firm.stocksymbol] && (
                        <tr>
                          <td
                            colSpan={4}
                            className={`sticky left-0 bg-[#f4f5f8] dark:bg-[#1a1d26] ${isCompactResearch ? "px-1 py-1" : "px-2 py-2"}`}
                          >
                            <div className="max-h-[120px] overflow-auto">
                              <table
                                className={`w-full ${isCompactResearch ? "text-[10px] leading-tight" : "text-sm"}`}
                              >
                                <thead>
                                  <tr className="text-left text-gray-500 dark:text-gray-400">
                                    <th className="px-1 py-0.5 font-medium font-figtree normal-case text-[#3D3D3D] dark:text-white">
                                      Date
                                    </th>
                                    <th className="px-1 py-0.5 font-medium font-figtree normal-case text-[#3D3D3D] dark:text-white">
                                      Author
                                    </th>
                                    <th className="px-1 py-0.5 text-center font-medium font-figtree normal-case text-[#3D3D3D] dark:text-white">
                                      Target
                                    </th>
                                    <th className="px-1 py-0.5 text-center font-medium font-figtree normal-case text-[#3D3D3D] dark:text-white">
                                      View
                                    </th>
                                    <th className="px-1 py-0.5 text-center font-medium font-figtree normal-case text-[#3D3D3D] dark:text-white">
                                      Upside
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {firm.details.map((detail, detailIndex) => (
                                    <tr
                                      key={detailIndex}
                                      className="border-t border-gray-200 dark:border-gray-700"
                                    >
                                      <td className="truncate px-1 py-0.5">
                                        {getFormattedDate(detail.date)}
                                      </td>
                                      <td
                                        className="truncate px-1 py-0.5"
                                        title={detail.author}
                                      >
                                        {detail.author}
                                      </td>
                                      <td
                                        className={`px-1 py-0.5 text-center ${detail.target < 0 ? "text-red-500" : "text-[#00A85F] dark:text-green-500"}`}
                                      >
                                        {detail.target || "-"}
                                      </td>
                                      <td
                                        className={`px-1 py-0.5 text-center ${detail.sell < 0 ? "text-red-500" : "text-[#00A85F] dark:text-green-500"}`}
                                      >
                                        {detail.sell
                                          ? "Sell"
                                          : detail.buy
                                            ? "Buy"
                                            : "-"}
                                      </td>
                                      <td
                                        className={`px-1 py-0.5 text-center font-medium ${
                                          detail.target_achieved
                                            ? "text-[#00A85F] dark:text-green-500"
                                            : detail.percent_achieved
                                              ? "text-black dark:text-white"
                                              : "text-yellow-500"
                                        }`}
                                      >
                                        {getUpsideRemaining(detail)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Gauge Chart */}
      {!isResearchFirm && !hideGauge && (
        <div className="bg-white dark:bg-[#121419] rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm xl:col-span-2 2xl:col-span-1">
          <div className="px-2 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-sm font-semibold">Overall Score</h2>
          </div>
          <div className="relative flex h-[250px] lg:h-[300px] items-center justify-center">
            <div className="w-full h-[220px]">
              <GaugeChart darkMode={darkMode} value={score} />
            </div>
          </div>
        </div>
      )}

      {chartStock && isChartModalOpen && (
        <ChartModal
          isOpen={isChartModalOpen}
          onClose={closeModal}
          stock={lineChartData}
          darkMode={darkMode}
          selectedStock={chartStock}
          selectedDate={selectedRecordDate}
          isDashboardModal={true}
          isLoadingChart={isLoadingChart}
        />
      )}
    </div>
  );
};

export default PerformanceTable;
